import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Percent, TrendingUp, TrendingDown, Check, AlertTriangle, Search, Filter } from 'lucide-react';
import { Product } from '@/types/product';
import { productApi } from '@/services/productApi';
import { categoryApi } from '@/services/categoryApi';
import { formatPrice } from '@/utils/productUtils';
import toast from 'react-hot-toast';

type Operation = 'percentage_increase' | 'percentage_decrease' | 'fixed_increase' | 'fixed_decrease' | 'set_price';
type Target = 'basePrice' | 'campaignPrice';
type SelectionMode = 'category' | 'manual';

interface Category {
  id: string;
  name: string;
  parentId?: string;
}

interface UpdateResult {
  id: string;
  title: string;
  oldPrice: number;
  newPrice: number;
}

const OPERATIONS: { value: Operation; label: string; icon: React.ReactNode; description: string }[] = [
  { value: 'percentage_increase', label: 'Yüzde Artır', icon: <TrendingUp className="w-4 h-4" />, description: '% oranında artış' },
  { value: 'percentage_decrease', label: 'Yüzde Azalt', icon: <TrendingDown className="w-4 h-4" />, description: '% oranında indirim' },
  { value: 'fixed_increase', label: 'Sabit Tutar Ekle', icon: <DollarSign className="w-4 h-4" />, description: '₺ tutarında artış' },
  { value: 'fixed_decrease', label: 'Sabit Tutar Düş', icon: <DollarSign className="w-4 h-4" />, description: '₺ tutarında indirim' },
  { value: 'set_price', label: 'Sabit Fiyat Ata', icon: <DollarSign className="w-4 h-4" />, description: 'Tüm fiyatları bu değere eşitle' },
];

export default function BulkPriceUpdate() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('category');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [operation, setOperation] = useState<Operation>('percentage_increase');
  const [value, setValue] = useState<string>('');
  const [target, setTarget] = useState<Target>('basePrice');
  const [searchTerm, setSearchTerm] = useState('');

  // Result state
  const [results, setResults] = useState<UpdateResult[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prodsResult, cats] = await Promise.all([
        productApi.getAllProducts(),
        categoryApi.getCategories(),
      ]);
      setProducts(prodsResult.products);
      setCategories(cats);
    } catch (error: any) {
      toast.error(error.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Products filtered by category or search
  const filteredProducts = useMemo(() => {
    let filtered = products;
    if (selectionMode === 'category' && selectedCategoryId) {
      filtered = filtered.filter(p => p.categories?.includes(selectedCategoryId));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(term) ||
        p.sku?.toLowerCase().includes(term)
      );
    }
    return filtered.sort((a, b) => a.title.localeCompare(b.title, 'tr'));
  }, [products, selectionMode, selectedCategoryId, searchTerm]);

  // Affected products for preview
  const affectedProducts = useMemo(() => {
    if (selectionMode === 'category') {
      return selectedCategoryId ? filteredProducts : [];
    }
    return products.filter(p => selectedProductIds.has(p.id));
  }, [selectionMode, selectedCategoryId, filteredProducts, selectedProductIds, products]);

  const toggleProduct = (id: string) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedProductIds.size === filteredProducts.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // Preview new prices
  const previewResults = useMemo(() => {
    const val = parseFloat(value);
    if (isNaN(val) || val < 0) return [];
    return affectedProducts.map(p => {
      const current = target === 'basePrice' ? p.basePrice : (p.campaignPrice || 0);
      let newPrice: number;
      switch (operation) {
        case 'percentage_increase': newPrice = current * (1 + val / 100); break;
        case 'percentage_decrease': newPrice = current * (1 - val / 100); break;
        case 'fixed_increase': newPrice = current + val; break;
        case 'fixed_decrease': newPrice = current - val; break;
        case 'set_price': newPrice = val; break;
        default: newPrice = current;
      }
      return {
        id: p.id,
        title: p.title,
        oldPrice: current,
        newPrice: Math.max(0, Math.round(newPrice * 100) / 100),
      };
    });
  }, [affectedProducts, operation, value, target]);

  const canSubmit = affectedProducts.length > 0 && value !== '' && parseFloat(value) >= 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const params: any = {
        operation,
        value: parseFloat(value),
        target,
      };
      if (selectionMode === 'category') {
        params.categoryId = selectedCategoryId;
      } else {
        params.productIds = Array.from(selectedProductIds);
      }
      const result = await productApi.bulkPriceUpdate(params);
      setResults(result.results);
      setShowConfirm(false);
      toast.success(result.message);
    } catch (error: any) {
      toast.error(error.message || 'Toplu güncelleme başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Success result view
  if (results) {
    return (
      <div className="p-4 md:p-6 lg:p-8 w-full space-y-6 transition-colors">
        {/* Success Header Card */}
        <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl p-6 shadow-xs">
          <div className="flex items-center gap-3.5 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
              <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-emerald-950 dark:text-emerald-200">Güncelleme Başarıyla Tamamlandı</h2>
              <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-0.5">
                Toplam <span className="font-semibold">{results.length}</span> adet ürünün fiyatı başarıyla güncellendi ve kaydedildi.
              </p>
            </div>
          </div>
        </div>

        {/* Results Table Card */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
          <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">Güncellenen Ürün Detayları</h3>
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
              {results.length} Ürün
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold text-xs">
                <tr>
                  <th className="px-5 py-3.5">Ürün Adı</th>
                  <th className="px-5 py-3.5 text-right">Eski Fiyat</th>
                  <th className="px-5 py-3.5 text-right">Yeni Fiyat</th>
                  <th className="px-5 py-3.5 text-right">Değişim</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {results.map(r => {
                  const diff = r.newPrice - r.oldPrice;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition">
                      <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-slate-100">{r.title}</td>
                      <td className="px-5 py-3.5 text-right text-slate-500 dark:text-slate-400 font-mono">{formatPrice(r.oldPrice)}</td>
                      <td className="px-5 py-3.5 text-right font-semibold text-slate-900 dark:text-slate-100 font-mono">{formatPrice(r.newPrice)}</td>
                      <td className="px-5 py-3.5 text-right font-mono">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          diff > 0
                            ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400'
                            : diff < 0
                            ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                        }`}>
                          {diff > 0 ? '+' : ''}{formatPrice(diff)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            onClick={() => { setResults(null); setShowConfirm(false); setValue(''); setSelectedProductIds(new Set()); }}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition font-medium shadow-sm active:scale-95 flex items-center gap-2"
          >
            Yeni Güncelleme Yap
          </button>
          <button
            onClick={() => navigate('/admin/products')}
            className="px-5 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition font-medium active:scale-95"
          >
            Ürün Listesine Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 w-full space-y-6 transition-colors">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex items-center gap-3.5">
          <button
            onClick={() => navigate('/admin/products')}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition active:scale-95"
            title="Geri Dön"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Toplu Fiyat Güncelleme
            </h1>
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Kategori veya seçeceğiniz özel ürünler üzerinde toplu oran veya sabit tutar fiyat güncellemeleri yapın
            </p>
          </div>
        </div>

        {affectedProducts.length > 0 && (
          <div className="flex items-center gap-2 self-start sm:self-auto px-3.5 py-1.5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/60 rounded-xl text-emerald-700 dark:text-emerald-400 text-xs font-semibold">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            {affectedProducts.length} Ürün Etkilenecek
          </div>
        )}
      </div>

      {/* Main Grid: Left Settings Form (4 cols), Right Product List & Preview (8 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Settings Panel */}
        <div className="lg:col-span-4 space-y-5">
          {/* 1. Selection Mode */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm mb-3.5 flex items-center justify-between">
              <span>1. Seçim Yöntemi</span>
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setSelectionMode('category'); setSelectedProductIds(new Set()); }}
                className={`py-2.5 px-3 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-2 ${
                  selectionMode === 'category'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                Kategoriye Göre
              </button>
              <button
                onClick={() => { setSelectionMode('manual'); setSelectedCategoryId(''); }}
                className={`py-2.5 px-3 rounded-xl text-xs font-semibold transition flex items-center justify-center gap-2 ${
                  selectionMode === 'manual'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <Check className="w-4 h-4" />
                Elle Seçim
              </button>
            </div>

            {selectionMode === 'category' && (
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Hedef Kategori
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={e => setSelectedCategoryId(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                >
                  <option value="">Kategori seçin...</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.parentId ? '  └ ' : ''}{cat.name}
                    </option>
                  ))}
                </select>
                {selectedCategoryId && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">
                    Kategoride {filteredProducts.length} adet ürün bulundu.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* 2. Price Target */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm mb-3.5">
              2. Hedef Fiyat Alanı
            </h3>
            <div className="space-y-2.5">
              <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === 'basePrice'
                  ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30'
                  : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
              }`}>
                <input
                  type="radio"
                  name="target"
                  checked={target === 'basePrice'}
                  onChange={() => setTarget('basePrice')}
                  className="text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-semibold text-xs text-slate-900 dark:text-slate-100">Satış Fiyatı (Ana Fiyat)</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Ürünün varsayılan taban fiyatını günceller</p>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                target === 'campaignPrice'
                  ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30'
                  : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
              }`}>
                <input
                  type="radio"
                  name="target"
                  checked={target === 'campaignPrice'}
                  onChange={() => setTarget('campaignPrice')}
                  className="text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-semibold text-xs text-slate-900 dark:text-slate-100">Kampanya Fiyatı (İndirimli Fiyat)</p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">Ürünün kampanya/indirim fiyatını günceller</p>
                </div>
              </label>
            </div>
          </div>

          {/* 3. Operation Type */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm mb-3.5">
              3. İşlem Tipi
            </h3>
            <div className="space-y-2">
              {OPERATIONS.map(op => (
                <label key={op.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
                  operation === op.value
                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30'
                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                }`}>
                  <input
                    type="radio"
                    name="operation"
                    checked={operation === op.value}
                    onChange={() => setOperation(op.value)}
                    className="text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="flex items-center gap-2.5 flex-1">
                    <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      {op.icon}
                    </div>
                    <div>
                      <p className="font-semibold text-xs text-slate-900 dark:text-slate-100">{op.label}</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">{op.description}</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 4. Value Input */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-xs">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm mb-3.5">
              4. Değer Girin
            </h3>
            <div className="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                value={value}
                onChange={e => setValue(e.target.value)}
                placeholder={operation.includes('percentage') ? 'Örn: 15' : 'Örn: 50.00'}
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-xl px-3.5 py-3 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
              />
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm font-bold">
                {operation.includes('percentage') ? '%' : '₺'}
              </span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSubmit}
            className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-xl shadow-xs disabled:bg-slate-200 dark:disabled:bg-slate-800 disabled:text-slate-400 dark:disabled:text-slate-600 disabled:cursor-not-allowed transition active:scale-[0.99]"
          >
            {affectedProducts.length > 0
              ? `${affectedProducts.length} Ürünün Fiyatını Güncelle`
              : 'İşleme Başlamak İçin Ürün Seçin'}
          </button>
        </div>

        {/* Right Column - Product Preview & Table */}
        <div className="lg:col-span-8 space-y-4">
          {/* Search Bar for manual mode */}
          {selectionMode === 'manual' && (
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Ürün adı veya SKU ile filtreleyin..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition shadow-xs"
              />
            </div>
          )}

          {/* Table Container */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                {selectionMode === 'category' ? 'Kategori Ürünleri & Fiyat Önizleme' : 'Ürün Seçim Listesi'}
              </h3>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                {(selectionMode === 'manual' ? filteredProducts : affectedProducts).length} Ürün Listeleniyor
              </span>
            </div>

            <div className="overflow-x-auto" style={{ maxHeight: '650px', overflowY: 'auto' }}>
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold text-xs sticky top-0 backdrop-blur-xs">
                  <tr>
                    {selectionMode === 'manual' && (
                      <th className="px-4 py-3.5 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={filteredProducts.length > 0 && selectedProductIds.size === filteredProducts.length}
                          onChange={toggleAll}
                          className="rounded border-slate-300 dark:border-slate-700 text-emerald-600 focus:ring-emerald-500"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3.5">Ürün</th>
                    <th className="px-4 py-3.5 text-right">Mevcut {target === 'basePrice' ? 'Satış' : 'Kampanya'} Fiyatı</th>
                    {value !== '' && parseFloat(value) >= 0 && (
                      <th className="px-4 py-3.5 text-right">Tahmini Yeni Fiyat</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {(selectionMode === 'manual' ? filteredProducts : affectedProducts).map(product => {
                    const currentPrice = target === 'basePrice' ? product.basePrice : (product.campaignPrice || 0);
                    const preview = previewResults.find(r => r.id === product.id);
                    const isSelected = selectionMode === 'manual' ? selectedProductIds.has(product.id) : true;

                    return (
                      <tr
                        key={product.id}
                        onClick={() => selectionMode === 'manual' && toggleProduct(product.id)}
                        className={`hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition ${
                          selectionMode === 'manual' ? 'cursor-pointer' : ''
                        } ${isSelected && selectionMode === 'manual' ? 'bg-emerald-50/30 dark:bg-emerald-950/20' : ''}`}
                      >
                        {selectionMode === 'manual' && (
                          <td className="px-4 py-3.5 text-center">
                            <input
                              type="checkbox"
                              checked={selectedProductIds.has(product.id)}
                              onChange={() => toggleProduct(product.id)}
                              onClick={e => e.stopPropagation()}
                              className="rounded border-slate-300 dark:border-slate-700 text-emerald-600 focus:ring-emerald-500"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-10 h-10 rounded-xl object-cover border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center text-xs font-semibold text-slate-400">
                                Görsel Yok
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm leading-snug">{product.title}</p>
                              {product.sku && <p className="text-xs text-slate-400 font-mono mt-0.5">{product.sku}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-right font-mono font-medium text-slate-600 dark:text-slate-300">
                          {formatPrice(currentPrice)}
                        </td>
                        {value !== '' && parseFloat(value) >= 0 && preview && isSelected && (
                          <td className="px-4 py-3.5 text-right font-mono font-bold">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${
                              preview.newPrice > preview.oldPrice
                                ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400'
                                : preview.newPrice < preview.oldPrice
                                ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                            }`}>
                              {formatPrice(preview.newPrice)}
                            </span>
                          </td>
                        )}
                        {value !== '' && parseFloat(value) >= 0 && (!preview || !isSelected) && (
                          <td className="px-4 py-3.5 text-right text-slate-300 dark:text-slate-700">—</td>
                        )}
                      </tr>
                    );
                  })}
                  {(selectionMode === 'manual' ? filteredProducts : affectedProducts).length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-slate-400 dark:text-slate-500">
                        {selectionMode === 'category' && !selectedCategoryId
                          ? 'Fiyat önizlemesini görmek için sol panelden bir kategori seçin.'
                          : 'Bu kriterlere uygun ürün bulunamadı.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {selectionMode === 'manual' && selectedProductIds.size > 0 && (
              <div className="px-4 py-3 bg-emerald-50/80 dark:bg-emerald-950/40 border-t border-emerald-100 dark:border-emerald-900/40 text-xs text-emerald-800 dark:text-emerald-300 font-semibold flex items-center justify-between">
                <span>{selectedProductIds.size} adet ürün güncellenmek üzere seçildi</span>
                <button
                  onClick={() => setSelectedProductIds(new Set())}
                  className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline"
                >
                  Seçimleri Temizle
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full p-6 space-y-5">
            <div className="flex items-center gap-3.5">
              <div className="w-11 h-11 bg-amber-100 dark:bg-amber-950/60 rounded-2xl flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">Fiyat Güncelleme Onayı</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Toplu değişiklik veritabanına uygulanacak</p>
              </div>
            </div>

            <div className="space-y-2 text-xs text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                <span className="text-slate-500 dark:text-slate-400">Etkilenecek Ürün:</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{affectedProducts.length} adet</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-200/60 dark:border-slate-700/60">
                <span className="text-slate-500 dark:text-slate-400">İşlem Tipi:</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {OPERATIONS.find(o => o.value === operation)?.label} ({value}{operation.includes('percentage') ? '%' : '₺'})
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500 dark:text-slate-400">Hedef Alan:</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {target === 'basePrice' ? 'Satış Fiyatı (Ana Fiyat)' : 'Kampanya Fiyatı'}
                </span>
              </div>
            </div>

            <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-xl p-3 font-medium">
              ⚠️ Bu işlem seçilen ürünlerin fiyatını doğrudan güncelleyecektir. Devam etmek istediğinizden emin misiniz?
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                className="flex-1 py-2.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-medium text-xs hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                İptal
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold text-xs hover:bg-emerald-700 transition disabled:bg-emerald-400 active:scale-95 shadow-xs"
              >
                {submitting ? 'Güncelleniyor...' : 'Evet, Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

